import type { ContextMenuItem } from '@charting-library'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useId, useRef, useState } from 'react'

import { OfflineError } from '@/app/query-client'
import { toast } from '@/components/ui/toast-store'
import type { AlertLevelPreset } from '@/features/alerts/components/AlertForm'
import NewAlertModal from '@/features/alerts/components/NewAlertModal'
import {
  ChartPanelContext,
  type ChartPanelApi,
} from '@/features/chart-rail/components/ChartPanelContext'
import ChartPanels from '@/features/chart-rail/components/ChartPanels'
import { createBarClicks, sessionOfBar } from '@/features/chart-rail/lib/bar-clicks'
import { createChartEvents } from '@/features/chart-rail/lib/chart-events'
import { readLastPanel, rememberLastPanel } from '@/features/chart-rail/lib/lastPanel'
import { chartSearchSchema, type PanelId } from '@/features/chart-rail/lib/panelIds'
import type { SymbolKind } from '@/features/chart-rail/lib/panels'
import ChartPanesLayer from '@/features/chart-rail/panes/ChartPanesLayer'
import { createPaneFeed } from '@/features/chart-rail/panes/feed'
import { createPaneSwitches } from '@/features/chart-rail/panes/switches'
import { useTabQuote, type TabQuote } from '@/features/chart-tabs/api/useTabQuote'
import ChartTabStrip from '@/features/chart-tabs/components/ChartTabStrip'
import NewTabPage from '@/features/chart-tabs/components/NewTabPage'
import { chartTabId, chartTabPanelId } from '@/features/chart-tabs/lib/tabIds'
import { SCREENER_KEY, symbolOf, type TabKey } from '@/features/chart-tabs/lib/tabsModel'
import { useChartTabs } from '@/features/chart-tabs/lib/useChartTabs'
import { servedResolution } from '@/features/trading-chart/api/chartMappers'
import { chartSymbolQueryOptions } from '@/features/trading-chart/api/queries'
import { createDatafeed } from '@/features/trading-chart/lib/datafeed'
import type { PriceBasis } from '@/features/trading-chart/api/chartApi'
import {
  readChartPriceBasis,
  rememberChartPriceBasis,
  rememberLastChartSymbol,
} from '@/features/trading-chart/lib/lastUsed'
import { createSaveLoadAdapter } from '@/features/trading-chart/lib/saveLoadAdapter'
import TradingChart, {
  type TradingChartHandle,
  type TradingChartHeaderButton,
} from '@/features/trading-chart/components/TradingChart'
import { useIsDesktop, useMediaQuery } from '@/hooks/useMediaQuery'
import { isEmbedded } from '@/lib/embed/bridge'
import { formatNPR } from '@/lib/format'
import { cmp, money } from '@/lib/money'
import { cn } from '@/lib/utils'
import { loadChartScreen } from '@/screens/chart/ChartScreen.loader'
import ChartLoadError from '@/screens/chart/components/ChartLoadError'
import ChartNotFound from '@/screens/chart/components/ChartNotFound'
import ChartScreenerTab from '@/screens/chart/components/ChartScreenerTab'
import ChartSkeleton from '@/screens/chart/components/ChartSkeleton'

/**
 * `/chart/$symbol` — the full-page chart.
 *
 * The URL's symbol is upper-cased here so `/chart/nabil` and `/chart/NABIL`
 * are one chart (the backend keys on the upper-case ticker). The chart mounts
 * at once, while the loader's symbol read is still out: the library and its
 * frame take longer to start than the read takes, and `resolveSymbol` waits
 * on the same query. `null` is mm-be saying it lists no such symbol, and swaps
 * the chart for the not-found branch. A failed read with nothing to draw goes
 * to the route error page, as it did when the loader awaited it.
 *
 * Over the chart runs the tab strip (features/chart-tabs, owner 2026-10-30): the pinned Advanced
 * screener, a tab per charted symbol, and the New tab. The URL says which shows: a chart tab is
 * its `/chart/$symbol`, the other two are `?view=` on the same route. The chart stays MOUNTED
 * under those two, hidden and inert, with its panels: one TradingView widget for every tab, so
 * coming back is instant and the member's drawings and zoom survive. A chart tab switch is the
 * widget's own `setSymbol`, as an in-chart pick is.
 *
 * Inside the Flutter app there is no strip: the app owns the screen around a single chart.
 */
function ChartScreen() {
  const { symbol: raw } = Route.useParams()
  const symbol = raw.toUpperCase()
  const { view } = Route.useSearch()
  const navigate = Route.useNavigate()
  const idBase = useId()
  const { data: info, error, fetchStatus } = useQuery(chartSymbolQueryOptions(symbol))

  // Every tab move replaces the entry, as an in-chart symbol pick does: one chart, one entry.
  const go = (key: TabKey) => {
    const target = symbolOf(key)
    void navigate({
      to: '/chart/$symbol',
      params: { symbol: target ?? symbol },
      search: (prev) => ({
        ...prev,
        view: target !== null ? undefined : key === SCREENER_KEY ? 'screener' : 'new',
      }),
      replace: true,
    })
  }
  const tabs = useChartTabs(symbol, view, go)

  // Not while a retry is out: the error page's Try again refetches this query
  // before it re-renders the page.
  if (info === undefined && fetchStatus !== 'fetching') {
    // The read waits for a network that is not there: say so, as the loader's
    // ensureRouteData did, rather than spin until the chart gives up.
    if (fetchStatus === 'paused') throw new OfflineError()
    if (error !== null) throw error
  }
  const chart =
    info === null ? (
      <ChartNotFound symbol={symbol} />
    ) : (
      <ChartBody symbol={symbol} onFollowSymbol={tabs.followSymbol} />
    )
  if (isEmbedded()) return chart

  const covered = view !== undefined
  return (
    <>
      <ChartTabStrip tabs={tabs} idBase={idBase} />
      <div
        role="tabpanel"
        id={chartTabPanelId(idBase)}
        aria-labelledby={chartTabId(idBase, tabs.showing)}
        className="relative flex min-h-0 flex-1"
      >
        <div className={cn('flex min-h-0 min-w-0 flex-1', covered && 'invisible')} inert={covered}>
          {chart}
        </div>
        {view === 'screener' ? (
          <div className="absolute inset-0 flex flex-col bg-background">
            <ChartScreenerTab onOpenSymbol={tabs.openSymbol} />
          </div>
        ) : view === 'new' ? (
          <div className="absolute inset-0">
            <NewTabPage recent={tabs.state.recent} onPick={tabs.pickInNewTab} />
          </div>
        ) : null}
      </div>
    </>
  )
}

/**
 * The panel a chart opened with no `?panel=` starts on: the one the member left open, or At a
 * Glance on a first visit where there is room to dock it. Nothing under `md`, where a panel is a
 * sheet over the chart and must be the member's own act. A remembered close stays closed.
 */
const startingPanel = (wide: boolean, docked: boolean): PanelId | undefined => {
  if (!wide) return undefined
  const last = readLastPanel()
  if (last === 'none') return undefined
  return last ?? (docked ? 'glance' : undefined)
}

/** Centred lines of uneven length: a price series levelled out, or not. 28px, as the library's. */
const BASIS_ICON = ['M9 9.5h10', 'M7 12.5h14', 'M9 15.5h10', 'M11 18.5h6'] as const

/** The header's Adj / Unadj: the basis on screen, and a press switches to the other. */
const BASIS_BUTTON: Record<PriceBasis, Omit<TradingChartHeaderButton, 'onClick'>> = {
  adjusted: {
    icon: BASIS_ICON,
    text: 'Adj',
    title: 'Adjusted prices: past bars adjusted for bonus and right issues. Switch to as traded',
  },
  traded: {
    icon: BASIS_ICON,
    text: 'Unadj',
    title: 'As-traded prices: past bars before bonus and right adjustments. Switch to adjusted',
  },
}

/** The library draws an item whose text is `-` as a divider; its click never fires. */
const MENU_DIVIDER: ContextMenuItem = { position: 'top', text: '-', click: () => undefined }

/**
 * "Add alert at Rs 536.40 (below Rs 547.00)" for the price under a right-click, snapped to the
 * stock's tick (`minmov / pricescale`) so the level is one NEPSE can trade at, and above or below
 * the last price as the alert would fire. A stock only: an index takes no alert.
 */
const alertMenuItems = (
  price: number,
  quote: TabQuote | null | undefined,
  info: { minmov: number; pricescale: number } | null | undefined,
  onPick: (level: AlertLevelPreset) => void,
): ContextMenuItem[] => {
  if (quote?.kind !== 'stock' || info === undefined || info === null || price <= 0) return []
  const step = info.minmov / info.pricescale
  const decimals = Math.max(0, Math.round(Math.log10(info.pricescale)))
  const level = (Math.round(price / step) * step).toFixed(decimals)
  const condition = cmp(money(level), quote.price) < 0 ? 'below' : 'above'
  return [
    {
      position: 'top',
      text: `Add alert at ${formatNPR(money(level))} (${condition} ${formatNPR(quote.price)})`,
      click: () => {
        onPick({ condition, price: level })
      },
    },
    MENU_DIVIDER,
  ]
}

/**
 * The symbol the panels describe, and whether it is a stock or an index. The URL's symbol moves
 * at once, but its kind is known only when its symbol read lands (a fresh pick is a round trip),
 * and a tab handed a new symbol under a guessed kind would fire stock reads for an index. So the
 * panels stay on the last symbol whose kind is known until the new one's is, and keep their state
 * across the switch; `null` before any is known (a cold open), when the panel shows its skeleton.
 */
function useChartedSymbol(
  symbol: string,
  info: { type: string } | null | undefined,
): { symbol: string; kind: SymbolKind } | null {
  const [last, setLast] = useState<{ symbol: string; kind: SymbolKind } | null>(null)
  const kind: SymbolKind | null =
    info === undefined || info === null ? null : info.type === 'index' ? 'index' : 'stock'
  if (kind !== null && (last?.symbol !== symbol || last.kind !== kind)) {
    const next = { symbol, kind }
    setLast(next)
    return next
  }
  return last
}

/**
 * The datafeed and the save/load adapter are built once per mount: the widget
 * reads each when it is constructed and never again, so a new pair per render
 * would only be garbage. A symbol picked inside the chart replaces the URL
 * rather than pushing it (one chart, one history entry), and the router keeps
 * this component mounted across the param change — a remount would destroy
 * and rebuild the widget, losing the member's drawings and zoom. The search
 * rides along, so the open panel survives a symbol switch.
 *
 * `min-h-0` on the region is what lets the chart shrink inside the
 * viewport-high column instead of growing past it; the bottom safe-area
 * inset keeps the library's time axis clear of the iOS home indicator.
 * `min-w-0` lets it give up width to the docked panel beside it.
 *
 * The open panel lives in `?panel=` and every change replaces the entry. A
 * visit with none starts on `startingPanel`, decided in the first render
 * rather than an effect: the widget is built at its container's size, and a
 * panel docking a frame later would reflow the chart a second time.
 */
function ChartBody({
  symbol,
  onFollowSymbol,
}: {
  symbol: string
  /** The showing chart tab follows a symbol picked inside the chart, before the URL moves. */
  onFollowSymbol: (symbol: string) => void
}) {
  const client = useQueryClient()
  const navigate = Route.useNavigate()
  const chart = useRef<TradingChartHandle>(null)
  const { panel } = Route.useSearch()
  const { data: info } = useQuery(chartSymbolQueryOptions(symbol))
  const charted = useChartedSymbol(symbol, info)
  const quote = useTabQuote(symbol)
  const [alertAt, setAlertAt] = useState<AlertLevelPreset | null>(null)
  const wide = useMediaQuery('(width >= 48rem)')
  const docked = useIsDesktop()
  // State for the button, and a ref for the datafeed, which reads it on the chart's next request.
  const [priceBasis, setPriceBasis] = useState(readChartPriceBasis)
  const basisRef = useRef(priceBasis)

  // Shown until the URL carries it, then dropped: from there the URL alone says what is open.
  const [starting, setStarting] = useState(() =>
    panel === undefined ? startingPanel(wide, docked) : undefined,
  )
  if (starting !== undefined && panel !== undefined) setStarting(undefined)
  const shownPanel = panel ?? starting

  useEffect(() => {
    if (starting === undefined || panel !== undefined) return
    void navigate({ search: (prev) => ({ ...prev, panel: starting }), replace: true })
  }, [navigate, panel, starting])

  // The refs are read only inside the toast's click and the datafeed's requests, long after
  // render; the rule cannot see that through the factory closure.
  // eslint-disable-next-line react-hooks/refs
  const [datafeed] = useState(() =>
    createDatafeed(client, {
      priceBasis: () => basisRef.current,
      onBarsError: () => {
        // One bar: toast() already folds an identical message into the one on
        // screen, so a chart that fails on every scroll does not stack them.
        toast.error("Couldn't load prices", {
          label: 'Retry',
          onClick: () => chart.current?.resetData(),
        })
      },
    }),
  )
  const [saveLoadAdapter] = useState(() => createSaveLoadAdapter(client))
  // Built once: panels subscribe from effects, and a new emitter per render would drop them.
  const [chartEvents] = useState(createChartEvents)
  const [barClicks] = useState(createBarClicks)
  // Built once, as the datafeed: the widget registers the pane indicators when it is built.
  const [paneFeed] = useState(createPaneFeed)
  const [paneSwitches] = useState(createPaneSwitches)

  const showSymbol = (next: string) => {
    onFollowSymbol(next)
    void navigate({
      to: '/chart/$symbol',
      params: { symbol: next },
      search: (prev) => prev,
      replace: true,
    })
  }

  const onSymbolChange = (next: string) => {
    rememberLastChartSymbol(next)
    showSymbol(next)
  }

  const switchBasis = () => {
    const next = basisRef.current === 'adjusted' ? 'traded' : 'adjusted'
    basisRef.current = next
    rememberChartPriceBasis(next)
    setPriceBasis(next)
    chart.current?.resetData()
  }

  const setPanel = (next: PanelId | undefined) => {
    rememberLastPanel(next ?? 'none')
    void navigate({ search: (prev) => ({ ...prev, panel: next }), replace: true })
  }

  const panelApi: ChartPanelApi = {
    symbol: charted?.symbol ?? symbol,
    kind: charted?.kind ?? 'stock',
    openPanel: setPanel,
    closePanel: () => {
      setPanel(undefined)
    },
    pickSymbol: (next) => {
      showSymbol(next.toUpperCase())
    },
    chart: () => chart.current?.activeChart() ?? null,
    onChartEvent: chartEvents.subscribe,
    onBarClick: barClicks.subscribe,
    paneSwitches,
  }

  return (
    <ChartPanelContext value={panelApi}>
      <div className="relative flex min-h-0 flex-1">
        <main data-chart-plot className="min-h-0 min-w-0 flex-1 pb-(--safe-bottom)">
          <TradingChart
            ref={chart}
            symbol={symbol}
            datafeed={datafeed}
            saveLoadAdapter={saveLoadAdapter}
            onSymbolChange={onSymbolChange}
            onChartEvent={chartEvents.emit}
            onBarClick={(time) => {
              // Only a daily bar is one session; an intraday, weekly or monthly bar is not.
              const live = chart.current?.activeChart() ?? null
              if (live === null || servedResolution(live.resolution()) !== '1D') return
              barClicks.emit(sessionOfBar(time))
            }}
            headerButton={{ ...BASIS_BUTTON[priceBasis], onClick: switchBasis }}
            customIndicators={paneFeed.indicators}
            onPaletteApplied={() => {
              chartEvents.emit('palette')
            }}
            // The embedded chart's host app has no alert flow of ours to open.
            onContextMenu={(price) =>
              isEmbedded() ? [] : alertMenuItems(price, quote, info, setAlertAt)
            }
            pending={<ChartSkeleton appear={false} />}
            renderError={(retry) => <ChartLoadError retry={retry} />}
          />
        </main>
        <ChartPanels panel={shownPanel} onPanelChange={setPanel} kindKnown={charted !== null} />
        <ChartPanesLayer feed={paneFeed} kindKnown={charted !== null} />
      </div>
      <NewAlertModal
        open={alertAt !== null}
        onClose={() => {
          setAlertAt(null)
        }}
        presetSymbol={symbol}
        {...(alertAt === null ? {} : { presetLevel: alertAt })}
      />
    </ChartPanelContext>
  )
}

// No entrance, like the overlay: a cold open (every chart visit, since it opens
// in a tab of its own) already has index.html's static skeleton on screen, and
// a fade-in from nothing would blank it for 80ms first.
// Unlabelled like ChartBody's `main`: the region inside TradingChart names the
// chart once it exists, and the skeleton is its own labelled status until then.
// The tab strip's empty band above it, so the chart does not drop when the strip
// arrives (index.html's splash draws the same band).
function ChartPending() {
  return (
    <>
      {isEmbedded() ? null : (
        <div aria-hidden="true" className="h-11 shrink-0 border-b border-border bg-muted lg:h-10" />
      )}
      <main className="min-h-0 flex-1 pb-(--safe-bottom)">
        <ChartSkeleton appear={false} />
      </main>
    </>
  )
}

export const Route = createFileRoute('/_workspace/chart/$symbol')({
  validateSearch: chartSearchSchema,
  loader: loadChartScreen,
  pendingComponent: ChartPending,
  component: ChartScreen,
  // The router's default remounts a route on any path-param change
  // (app/router.tsx), which here would destroy the widget and rebuild it,
  // dropping the member's drawings and zoom on every in-chart symbol pick.
  // The widget follows `symbol` itself (`setSymbol`), so nothing is keyed.
  remountDeps: () => undefined,
})
